import { useTheme } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DSAlert from "views/system-design/DSAlert";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSSkeleton from "views/system-design/DSSkeleton";
import DSTypography from "views/system-design/DSTypography";
import sectionCardSx from "views/system-design/sectionCardSx";
import {
  RetailerChartRow,
  pluralize,
  totalWithoutId,
  truncate,
} from "../utils/retailerChart";
import { SEGMENT_LABELS, useSegmentColors } from "./useSegmentColors";

const ROW_HEIGHT = 48;
const AXIS_WIDTH = 230;
const NAME_MAX_CHARS = 28;

type Props = {
  rows: RetailerChartRow[];
  isLoading: boolean;
  errorMessage?: string;
  onRetry: () => void;
  selectedId?: string;
  onSelect: (id: string) => void;
};

type TickProps = {
  x?: number | string;
  y?: number | string;
  index?: number;
};

/** The retailer name with its "5 orders · 27% sold" line under it. Clicking selects it. */
const RetailerTick = ({
  x = 0,
  y = 0,
  row,
  selected,
  onSelect,
}: TickProps & {
  row?: RetailerChartRow;
  selected: boolean;
  onSelect: (id: string) => void;
}) => {
  const { palette } = useTheme();
  if (!row) return null;
  return (
    <g
      transform={`translate(${x},${y})`}
      style={{ cursor: "pointer" }}
      onClick={() => onSelect(row.id)}
    >
      <title>{row.name}</title>
      <text
        x={-8}
        y={-3}
        textAnchor="end"
        fontSize={13}
        fontWeight={selected ? 700 : 500}
        fill={palette.text.primary}
      >
        {truncate(row.name, NAME_MAX_CHARS)}
      </text>
      <text
        x={-8}
        y={13}
        textAnchor="end"
        fontSize={11}
        fill={palette.text.secondary}
      >
        {row.subtitle}
      </text>
    </g>
  );
};

type TooltipProps = {
  active?: boolean;
  payload?: { payload: RetailerChartRow }[];
};

const RetailerTooltip = ({ active, payload }: TooltipProps) => {
  const { palette } = useTheme();
  const colors = useSegmentColors();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  const lines: [keyof typeof SEGMENT_LABELS, number][] = [
    ["sold", row.sold],
    ["notSold", row.notSold],
    ["withoutId", row.withoutId],
  ];
  return (
    <DSBox
      sx={{
        backgroundColor: palette.paper.elevation8,
        borderRadius: "8px",
        p: 1.5,
        fontSize: 12,
      }}
    >
      <DSBox sx={{ fontWeight: 700, mb: 0.5 }}>{row.name}</DSBox>
      <DSBox sx={{ color: "text.secondary", mb: 0.5 }}>
        {pluralize(row.orders, "order")} · {row.clocksSent} clocks sent
      </DSBox>
      {lines.map(([key, value]) => (
        <DSBox key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: colors[key],
              display: "inline-block",
            }}
          />
          {SEGMENT_LABELS[key]}: {value}
        </DSBox>
      ))}
    </DSBox>
  );
};

/**
 * One stacked bar per retailer: sold, not sold yet, and no clock id yet add up
 * to the clocks we sent. Orders are a different unit, so they are in the line
 * under the name, not a segment. Click a bar (or a name) to open that retailer.
 */
const RetailerClocksChartCard = ({
  rows,
  isLoading,
  errorMessage,
  onRetry,
  selectedId,
  onSelect,
}: Props) => {
  const { palette } = useTheme();
  const colors = useSegmentColors();
  const missingIds = totalWithoutId(rows);
  const dimmed = (id: string) => (selectedId && id !== selectedId ? 0.35 : 1);

  const bar = (
    key: keyof typeof SEGMENT_LABELS,
    radius?: [number, number, number, number],
  ) => (
    <Bar
      key={key}
      dataKey={key}
      name={SEGMENT_LABELS[key]}
      stackId="clocks"
      fill={colors[key]}
      radius={radius}
      cursor="pointer"
      onClick={(_data: unknown, index: number) => {
        const row = rows[index];
        if (row) onSelect(row.id);
      }}
    >
      {rows.map((row) => (
        <Cell key={row.id} fillOpacity={dimmed(row.id)} />
      ))}
    </Bar>
  );

  return (
    <DSCard sx={sectionCardSx}>
      <DSCardContent>
        <DSTypography variant="h6" sx={{ mb: 1 }}>
          Clocks per retailer
        </DSTypography>

        {isLoading ? (
          <DSSkeleton variant="rounded" height={220} />
        ) : errorMessage ? (
          <DSAlert
            severity="error"
            action={
              <DSButton size="small" color="inherit" onClick={onRetry}>
                Retry
              </DSButton>
            }
          >
            {errorMessage}
          </DSAlert>
        ) : rows.length === 0 ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No retailer orders in this period
          </DSTypography>
        ) : (
          <>
            <ResponsiveContainer
              width="100%"
              height={rows.length * ROW_HEIGHT + 56}
            >
              <BarChart
                data={rows}
                layout="vertical"
                margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
                barCategoryGap="22%"
              >
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: palette.text.secondary }}
                  stroke={palette.white[24]}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={AXIS_WIDTH}
                  interval={0}
                  axisLine={false}
                  tickLine={false}
                  tick={(props: TickProps) => (
                    <RetailerTick
                      {...props}
                      row={rows[props.index ?? -1]}
                      selected={rows[props.index ?? -1]?.id === selectedId}
                      onSelect={onSelect}
                    />
                  )}
                />
                <Tooltip
                  content={<RetailerTooltip />}
                  cursor={{ fill: palette.white[12] }}
                />
                <Legend />
                {bar("sold")}
                {bar("notSold")}
                {bar("withoutId", [0, 3, 3, 0])}
              </BarChart>
            </ResponsiveContainer>
            {missingIds > 0 && (
              <DSTypography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 1 }}
              >
                {pluralize(missingIds, "clock")} (light grey) have no clock id
                yet, so they cannot be checked and count as not sold.
              </DSTypography>
            )}
          </>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export default RetailerClocksChartCard;
